/**
 * Un ZIP mínimo, sin comprimir (método STORE) — para el paquete de PDF de
 * `OD-0036`.
 *
 * ¿Por qué escribirlo y no traer una librería? Porque lo que hace falta es muy
 * poco y muy estable (la especificación APPNOTE de PKWARE no ha cambiado en
 * esta parte en treinta años), y porque una dependencia nueva en el Worker es
 * superficie que revisar. Y ¿por qué sin comprimir? Porque el contenido son
 * PDF, que ya llevan sus propios flujos, y comprimir otra vez cuesta CPU del
 * Worker para ganar muy poco.
 *
 * ```text
 * por cada fichero   cabecera local + bytes
 * al final           directorio central + fin de directorio central
 * ```
 *
 * Determinista: las mismas entradas dan los mismos bytes. Los nombres tienen
 * que ser ASCII imprimible —los construye quien llama, a partir de números de
 * factura o de los nombres fijos de un `.xlsx`—, así que no hace falta la marca
 * UTF-8; y un `../` se rechaza aunque nadie pueda colarlo.
 */

export type EntradaZip = {
  readonly nombre: string;
  readonly bytes: Uint8Array;
};

const TABLA_CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = (TABLA_CRC[(c ^ (bytes[i] as number)) & 0xff] as number) ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Fecha y hora en el formato de MS-DOS que pide la cabecera. */
function fechaDos(d: { readonly y: number; readonly m: number; readonly d: number }): { readonly hora: number; readonly fecha: number } {
  return { hora: 0, fecha: ((d.y - 1980) << 9) | (d.m << 5) | d.d };
}

/**
 * Tramos de ASCII imprimible separados por `/`: lo justo para `facturas/X.pdf`
 * y para las piezas de un `.xlsx` (`[Content_Types].xml`, `xl/_rels/...`).
 * Nada de `..`, ni ruta absoluta, ni `\`.
 */
const NOMBRE_VALIDO = /^[A-Za-z0-9._[\]-]+(\/[A-Za-z0-9._[\]-]+)*$/;

/**
 * Construye el ZIP. `dia` es `AAAA-MM-DD`: la fecha que llevan todas las
 * entradas, para que el resultado no dependa del reloj.
 */
export function construirZip(entradas: readonly EntradaZip[], dia: string): Uint8Array {
  const m = dia.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m === null) throw new Error(`dia ilegible para el ZIP: ${dia}`);
  const { hora, fecha } = fechaDos({ y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) });

  const vistos = new Set<string>();
  const locales: Uint8Array[] = [];
  const centrales: Uint8Array[] = [];
  let desplazamiento = 0;

  for (const e of entradas) {
    if (!NOMBRE_VALIDO.test(e.nombre) || e.nombre.includes("..")) throw new Error(`nombre no admitido en el ZIP: ${e.nombre}`);
    if (vistos.has(e.nombre)) throw new Error(`nombre repetido en el ZIP: ${e.nombre}`);
    vistos.add(e.nombre);
    const nombre = new TextEncoder().encode(e.nombre);
    const crc = crc32(e.bytes);
    const tam = e.bytes.length;

    const local = new Uint8Array(30 + nombre.length);
    const l = new DataView(local.buffer);
    l.setUint32(0, 0x04034b50, true); // firma de cabecera local
    l.setUint16(4, 10, true); // version necesaria: 1.0 (STORE)
    l.setUint16(6, 0, true); // flags
    l.setUint16(8, 0, true); // metodo: STORE
    l.setUint16(10, hora, true);
    l.setUint16(12, fecha, true);
    l.setUint32(14, crc, true);
    l.setUint32(18, tam, true); // comprimido
    l.setUint32(22, tam, true); // sin comprimir
    l.setUint16(26, nombre.length, true);
    l.setUint16(28, 0, true); // extra
    local.set(nombre, 30);

    const central = new Uint8Array(46 + nombre.length);
    const c = new DataView(central.buffer);
    c.setUint32(0, 0x02014b50, true); // firma del directorio central
    c.setUint16(4, 20, true); // hecho por: 2.0
    c.setUint16(6, 10, true); // version necesaria
    c.setUint16(8, 0, true);
    c.setUint16(10, 0, true);
    c.setUint16(12, hora, true);
    c.setUint16(14, fecha, true);
    c.setUint32(16, crc, true);
    c.setUint32(20, tam, true);
    c.setUint32(24, tam, true);
    c.setUint16(28, nombre.length, true);
    c.setUint16(30, 0, true); // extra
    c.setUint16(32, 0, true); // comentario
    c.setUint16(34, 0, true); // disco
    c.setUint16(36, 0, true); // atributos internos
    c.setUint32(38, 0, true); // atributos externos
    c.setUint32(42, desplazamiento, true);
    central.set(nombre, 46);

    locales.push(local, e.bytes);
    centrales.push(central);
    desplazamiento += local.length + tam;
  }

  const tamCentral = centrales.reduce((s, x) => s + x.length, 0);
  const fin = new Uint8Array(22);
  const f = new DataView(fin.buffer);
  f.setUint32(0, 0x06054b50, true); // fin del directorio central
  f.setUint16(8, entradas.length, true);
  f.setUint16(10, entradas.length, true);
  f.setUint32(12, tamCentral, true);
  f.setUint32(16, desplazamiento, true);

  const total = new Uint8Array(desplazamiento + tamCentral + fin.length);
  let p = 0;
  for (const trozo of [...locales, ...centrales, fin]) {
    total.set(trozo, p);
    p += trozo.length;
  }
  return total;
}
